import { FC, useEffect, useMemo, useState } from "react"
import If from "../../../if"
import ProgressBar from "../../../progress-bar"
import { FourColumnPerformanceSkeleton } from "../../../skeletons"
import ButtonGroup from "../../../button-group"
import PillTabs from "../../../pill-tabs"
import NoData from "../../../no-data"
import {
  useFetchTopLeadBreakdownTypeEmbedsQuery,
  useFetchTopLeadBreakdownTypeLOsQuery,
  useFetchTopLeadBreakdownTypeURLQuery
} from "../../../../../redux/queries"
import {
  TransformTopLeadBreakdown,
  useTransformTopLeadBreakdown
} from "../../../../hooks"
import { TopLeadBreakdownData } from "../../../../../types"

interface TopLeadBreakdownProps {
  title: string
  range: number
  orgId: string | undefined
  loid: string | undefined
  hasRangeFilter?: boolean
  contentHeight?: string
}

export enum TopLeadBreakdownTabs {
  URL = "breakdown-by-url",
  EMBEDS = "breakdown-by-embeds",
  LOS = "breakdown-by-los"
}

enum TopLeadBreakdownLabelTabs {
  URL = "URL",
  EMBEDS = "Embeds",
  LOS = "LO's"
}

const TopLeadBreakdown: FC<TopLeadBreakdownProps> = ({
  title,
  range,
  orgId,
  loid,
  hasRangeFilter = false,
  contentHeight = "330px"
}) => {
  const [isFetching, setIsFetching] = useState<boolean>(true)
  const [tab, setTab] = useState<string>(TopLeadBreakdownTabs.URL)
  const [transformedData, setTransformedData] = useState<
    TransformTopLeadBreakdown[]
  >([])
  const [days, setDays] = useState<number>(range)
  const [previousRange, setPreviousRange] = useState<number>(range + 30)
  const FILTER_BUTTONS = [
    {
      key: 90,
      label: "90 Days",
      disabled: false,
      onClick: () => {
        setDays(90)
        setPreviousRange(90 + 30)
      }
    },
    {
      key: 30,
      label: "30 Days",
      disabled: false,
      onClick: () => {
        setDays(30)
        setPreviousRange(30 + 30)
      }
    },
    {
      key: 7,
      label: "7 Days",
      disabled: false,
      onClick: () => {
        setDays(7)
        setPreviousRange(7 + 30)
      }
    }
  ]
  const fetchParams = (range: number) => ({
    range: range,
    org_id: orgId,
    loid: loid
  })
  const fetchOptions = (canSkip: boolean) => ({
    refetchOnMountOrArgChange: true,
    skip: canSkip
  })

  const { data: urlData, isFetching: isFetchingUrl } =
    useFetchTopLeadBreakdownTypeURLQuery(
      fetchParams(days),
      fetchOptions(tab !== TopLeadBreakdownTabs.URL)
    )
  const { data: urlPreviousData, isFetching: isFetchingUrlPrevious } =
    useFetchTopLeadBreakdownTypeURLQuery(
      fetchParams(previousRange),
      fetchOptions(tab !== TopLeadBreakdownTabs.URL)
    )
  const { data: embedsData, isFetching: isFetchingEmbeds } =
    useFetchTopLeadBreakdownTypeEmbedsQuery(
      fetchParams(days),
      fetchOptions(tab !== TopLeadBreakdownTabs.EMBEDS)
    )
  const { data: embedsPreviousData, isFetching: isFetchingEmbedsPrevious } =
    useFetchTopLeadBreakdownTypeEmbedsQuery(
      fetchParams(previousRange),
      fetchOptions(tab !== TopLeadBreakdownTabs.EMBEDS)
    )
  const { data: losData, isFetching: isFetchingLOs } =
    useFetchTopLeadBreakdownTypeLOsQuery(
      fetchParams(days),
      fetchOptions(tab !== TopLeadBreakdownTabs.LOS)
    )
  const { data: losPreviousData, isFetching: isFetchingLOsPrevious } =
    useFetchTopLeadBreakdownTypeLOsQuery(
      fetchParams(previousRange),
      fetchOptions(tab !== TopLeadBreakdownTabs.LOS)
    )

  const breakdownData: TopLeadBreakdownData[] | undefined = useMemo(() => {
    if (!isFetching) {
      switch (tab) {
        case TopLeadBreakdownTabs.URL:
          return urlData
        case TopLeadBreakdownTabs.EMBEDS:
          return embedsData
        case TopLeadBreakdownTabs.LOS:
          return losData
        default:
          return []
      }
    }
  }, [urlData, embedsData, losData, isFetching, tab])
  const previousBreakdownData: TopLeadBreakdownData[] | undefined =
    useMemo(() => {
      if (!isFetching) {
        switch (tab) {
          case TopLeadBreakdownTabs.URL:
            return urlPreviousData
          case TopLeadBreakdownTabs.EMBEDS:
            return embedsPreviousData
          case TopLeadBreakdownTabs.LOS:
            return losPreviousData
          default:
            return []
        }
      }
    }, [isFetching, tab, urlPreviousData, embedsPreviousData, losPreviousData])
  const maxCount = useMemo(
    () =>
      transformedData?.length > 0
        ? Math.max(...transformedData.map(data => data.lead_count))
        : 0,
    [transformedData]
  )
  const currentTransformedData = useTransformTopLeadBreakdown(
    tab,
    breakdownData,
    previousBreakdownData
  )

  useEffect(() => {
    setTransformedData(currentTransformedData)
  }, [currentTransformedData])

  useEffect(() => {
    setIsFetching(
      isFetchingUrl ||
        isFetchingEmbeds ||
        isFetchingLOs ||
        isFetchingUrlPrevious ||
        isFetchingEmbedsPrevious ||
        isFetchingLOsPrevious
    )
  }, [
    isFetchingEmbeds,
    isFetchingEmbedsPrevious,
    isFetchingLOs,
    isFetchingLOsPrevious,
    isFetchingUrl,
    isFetchingUrlPrevious
  ])

  return (
    <div className="bg-white flex flex-col flex-1 px-6 pt-4 pb-7 rounded-md overflow-hidden">
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-0 items-center justify-between">
        <div className="flex self-start mt-2.5 text-base font-medium">
          {title}
        </div>
        <div className="flex-1 w-full">
          {hasRangeFilter && (
            <div className="flex lg:justify-end lg:ml-4 mb-2">
              <ButtonGroup
                className="lg:flex justify-end lg:ml-4"
                buttons={FILTER_BUTTONS}
                active={days}
              />
            </div>
          )}
          <PillTabs
            currentTab={tab}
            className="lg:justify-end lg:ml-4"
            tabs={[
              {
                id: TopLeadBreakdownTabs.URL,
                label: TopLeadBreakdownLabelTabs.URL
              },
              {
                id: TopLeadBreakdownTabs.EMBEDS,
                label: TopLeadBreakdownLabelTabs.EMBEDS
              },
              {
                id: TopLeadBreakdownTabs.LOS,
                label: TopLeadBreakdownLabelTabs.LOS
              }
            ]}
            onChange={setTab}
          />
        </div>
      </div>
      <If
        condition={isFetching}
        then={<FourColumnPerformanceSkeleton className="mt-px pt-11" />}
        else={
          <If
            condition={!transformedData || transformedData.length === 0}
            then={<NoData />}
            else={
              <div
                className="mt-7 overflow-y-auto scroll-smooth"
                style={{ height: contentHeight }}
              >
                <div className="w-full grid gap-y-6 py-2">
                  {transformedData.map((breakdownData, index) => (
                    <ProgressBar
                      key={index}
                      count={breakdownData.lead_count}
                      maxCount={maxCount}
                      label={
                        tab === TopLeadBreakdownTabs.URL
                          ? breakdownData.url
                          : breakdownData.name
                      }
                      netPercentage={breakdownData.netPercentage}
                      isPositive={breakdownData.isPositive}
                    />
                  ))}
                </div>
              </div>
            }
          />
        }
      />
    </div>
  )
}

export default TopLeadBreakdown
